/* eslint-disable import/no-cycle */
import {
  WidgetTypeConfig,
  ConfigurationPanelStructureItemEnum,
  BackgroundImageOptionTypeEnum,
  ComponentNameEnum,
  ElementPartTypeEnum,
  StepperSignEnum,
  TemplateStructureEnum,
} from '@/types';
import getColorFieldConfig from '@/components/template-builder/config/fields/color-config';
import mediaDisplayFieldConfig from '@/components/template-builder/config/fields/media-display-config';
import getStepperFieldConfig from '@/components/template-builder/config/fields/stepper-config';
import getInputVariablesFieldConfig from '@/components/template-builder/config/fields/input-variables-config';
import getGamePrizesEditorFieldConfig from '@/components/template-builder/config/fields/game-prizes-editor-config';
import getGameTestButtonFieldConfig from '@/components/template-builder/config/fields/game-test-button-config';
import getGameStockEditorFieldConfig from '@/components/template-builder/config/fields/game-stock-editor-config';
// eslint-disable-next-line import/no-cycle
import getBackgroundImageFieldConfig from '@/components/template-builder/config/fields/background-image-config';
import {
  setCssProperty,
  setMediaDisplay,
  setContent,
  setAttribute,
  setGameCssProperty,
  setGameScratchCardAttribute,
  setGameCoverImageProp,
  setGameCoverIconProp,
} from '@/components/template-builder/setters';
import {
  getClassByName,
  getCssPropertyByName,
  getAttributeByName,
  getContent,
  getAlwaysTrue,
  getGameCssProperty,
  getGameCoverImageProp,
  getGameCoverIconProp,
} from '@/components/template-builder/getters';
import getConditionsAndRepetitionGroup from '@/components/template-builder/config/fields/conditions-repetition-group-helper';
// eslint-disable-next-line import/no-cycle
import getWidgetCommonStylesGroup from '@/components/template-builder/config/fields/widget-common-styles-config';

const displayWidgetGameScratchCardConfigV4: WidgetTypeConfig = {
  tabPanel: {
    label: 'templateBuilder.widgetsHeaders.gameScratchCard',
    items: [
      {
        type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
        tabItemType: TemplateStructureEnum.GROUP,
        label: 'templateBuilder.configs.gameConfiguration',
        scrollable: true,
        tabPanel: {
          items: [
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gamePrizes',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: '',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-prizes',
                    getter: getAttributeByName,
                    setters: [setAttribute],
                  }],
                  ...getGamePrizesEditorFieldConfig({ showColorPicker: false }),
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameSettings',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.gameScratchThreshold',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-reveal-threshold',
                    getter: getAttributeByName,
                    setters: [setAttribute],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PERCENT, 5, 20, 80),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PERCENT, 5, 20, 80).options,
                    defaultValue: 50,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameScratchBrushSize',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-brush-size',
                    getter: getAttributeByName,
                    setters: [setAttribute],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 5, 10, 100),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 5, 10, 100).options,
                    defaultValue: 30,
                  },
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameMessages',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.gameScratchCoverText',
                  selector: '{SELECTOR} .spm_game_scratch_card_cover_text',
                  properties: [{ name: 'content', getter: getContent, setters: [setContent] }],
                  ...getInputVariablesFieldConfig('Grattez ici !', [], false, false),
                },
                {
                  label: 'templateBuilder.fields.gameScratchCoverHint',
                  selector: '{SELECTOR} .spm_game_scratch_card_cover_hint',
                  properties: [{ name: 'content', getter: getContent, setters: [setContent] }],
                  ...getInputVariablesFieldConfig('Utilisez votre souris ou votre doigt', [], false, false),
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameStock',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: '',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-prizes',
                    getter: getAttributeByName,
                    setters: [setAttribute],
                  }],
                  ...getGameStockEditorFieldConfig({ gameType: 'scratchCard' }),
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameTest',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.testGame',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'test',
                    getter: getAlwaysTrue,
                    setters: [],
                  }],
                  ...getGameTestButtonFieldConfig('scratchCard'),
                },
              ],
            },
          ],
        },
      },
      {
        type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
        tabItemType: TemplateStructureEnum.GROUP,
        label: 'templateBuilder.configs.gameScratchCardStyles',
        tabPanel: {
          items: [
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameScratchCardAppearance',
              selector: 'body #spm_body #{ID}',
              canSave: true,
              elementPartType: ElementPartTypeEnum.STYLES,
              items: [
                {
                  label: 'templateBuilder.fields.gameScratchCardWidth',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-card-width',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 10, 150, 500),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 10, 150, 500).options,
                    defaultValue: 300,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameScratchCardHeight',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-card-height',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 10, 100, 400),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 10, 100, 400).options,
                    defaultValue: 200,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameScratchCoverColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-cover-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#C0C0C0', true, false, true),
                },
                {
                  label: 'templateBuilder.fields.gameScratchCoverImage',
                  selector: '{SELECTOR}',
                  properties: [
                    { name: 'background-image', getter: getGameCoverImageProp, setters: [setGameCoverImageProp] },
                    { name: 'background-size', getter: getGameCoverImageProp, setters: [setGameCoverImageProp] },
                    { name: 'background-repeat', getter: getGameCoverImageProp, setters: [setGameCoverImageProp] },
                  ],
                  ...getBackgroundImageFieldConfig(BackgroundImageOptionTypeEnum.SIZE_REPEAT),
                },
                {
                  label: 'templateBuilder.fields.gameScratchCoverIcon',
                  selector: '{SELECTOR}',
                  properties: [
                    { name: 'background-image', getter: getGameCoverIconProp, setters: [setGameCoverIconProp] },
                  ],
                  ...getBackgroundImageFieldConfig(BackgroundImageOptionTypeEnum.NO_OPTION),
                },
                {
                  label: 'templateBuilder.fields.gameScratchCoverTexture',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-cover-texture',
                    getter: getAttributeByName,
                    setters: [setGameScratchCardAttribute],
                  }],
                  componentName: ComponentNameEnum.CHECKBOX,
                  options: {
                    defaultValue: true,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameScratchCoverTextColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-cover-text-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#ffffff', true, false),
                },
                {
                  label: 'templateBuilder.fields.gameScratchBorderRadius',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-border-radius',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 30),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 30).options,
                    defaultValue: 16,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameScratchBorderColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-border-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#d4a017', true, false, true),
                },
                {
                  label: 'templateBuilder.fields.gameScratchBorderWidth',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-border-width',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 8),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 8).options,
                    defaultValue: 3,
                  },
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameScratchCardRevealStyles',
              selector: 'body #spm_body #{ID}',
              canSave: true,
              elementPartType: ElementPartTypeEnum.STYLES,
              items: [
                {
                  label: 'templateBuilder.fields.gameScratchBackgroundColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-background-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#ffffff', true, false, true),
                },
                {
                  label: 'templateBuilder.fields.gameScratchResultTextColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-result-text-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#1a1a1a', true, false),
                },
                {
                  label: 'templateBuilder.fields.gameScratchResultFontSize',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-result-font-size',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 12, 48),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 12, 48).options,
                    defaultValue: 24,
                  },
                },
              ],
            },
            getWidgetCommonStylesGroup(),
          ],
        },
      },
      getConditionsAndRepetitionGroup('body #spm_body #{ID}'),
    ],
  },
};

export default displayWidgetGameScratchCardConfigV4;
